import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { GridIcon, PlusIcon, TargetIcon } from './icons'

export interface NavCounts {
  /** Openstaande challenges; het bolletje naast "Challenges". */
  challenges: number
}

/**
 * De onderbalk op mobiel (ronde 6A).
 *
 * Drie gelijke vakken naast elkaar, elk met het teken boven het woord. Dat is de
 * vorm die elke telefoon-app gebruikt, en er is een reden voor: alles staat in
 * hetzelfde ritme, dus er is geen kant die zwaarder weegt.
 *
 * Het ging hiervoor twee keer mis op datzelfde punt. Eerst zaten de drie in één
 * afgeronde capsule, wat las als een segmented control met een vreemde knop
 * erin. Daarna stond de actie als grote ronde knop tussen twee brede tekstvakken,
 * en dan is het midden zwaarder dan de randen — scheef, ook al staat hij precies
 * in het midden.
 *
 * Nu is het middelste vak een vak als de andere. De actie is nog steeds te
 * onderscheiden — een vol vlak in de accentkleur in plaats van een lijntekening —
 * maar hij neemt niet meer ruimte in dan zijn buren.
 */
export function BottomNav({ counts, onAdd }: { counts: NavCounts; onAdd?: () => void }) {
  return (
    // Boven en onder evenveel, plus de echte veilige zone eronder. Die stond
    // hier als vaste `pb-5`, bedoeld voor de streep onderaan een iPhone — maar
    // die maat geldt alleen op zo'n toestel. Overal elders werd het gewoon lucht:
    // elf pixels boven het teken tegen vierentwintig onder het woord.
    //
    // `env()` geeft nul op een scherm zonder streep, dus dan staat het gelijk, en
    // precies genoeg waar er wél iets zit. De meta-tag in index.html heeft
    // `viewport-fit=cover`, anders levert `env()` altijd nul.
    <nav className="border-line-soft bg-shell sticky bottom-0 z-20 mt-auto flex shrink-0 items-stretch border-t px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] lg:hidden">
      <BottomNavItem to="album" label="Galerij" icon={<GridIcon className="size-5.5" />} />

      {/* Zonder actie blijven er twee vakken over die samen de balk vullen —
          wie een privé galerij bekijkt kan niets toevoegen, dus staat er ook
          geen knop die dat belooft. */}
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="text-ink-soft flex flex-1 cursor-pointer flex-col items-center gap-1 rounded-2xl py-1 active:scale-95"
        >
          <span className="bg-accent text-accent-ink flex size-7 items-center justify-center rounded-full">
            <PlusIcon className="size-4" />
          </span>
          <span className="text-[11px] leading-none">Toevoegen</span>
        </button>
      )}

      <BottomNavItem
        to="challenges"
        label="Challenges"
        icon={<TargetIcon className="size-5.5" />}
        badge={counts.challenges}
      />
    </nav>
  )
}

function BottomNavItem({
  to,
  label,
  icon,
  badge,
}: {
  to: string
  label: string
  icon: ReactNode
  badge?: number
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        'flex flex-1 flex-col items-center gap-1 rounded-2xl py-1 ' +
        (isActive ? 'text-accent' : 'text-ink-soft')
      }
    >
      {({ isActive }) => (
        <>
          {/* Vaste hoogte, zodat de drie woorden eronder op dezelfde lijn staan
              ook als het ene teken een pixel hoger tekent dan het andere. */}
          <span className="relative flex h-7 items-center">
            {icon}

            {badge !== undefined && badge > 0 && (
              <span className="bg-accent text-accent-ink absolute -top-0.5 -right-2.5 rounded-full px-1.25 py-px text-[10px] font-bold">
                {badge}
              </span>
            )}
          </span>

          <span className={'text-[11px] leading-none ' + (isActive ? 'font-semibold' : '')}>
            {label}
          </span>
        </>
      )}
    </NavLink>
  )
}

/** Dezelfde twee bestemmingen als lijst in de zijnavigatie (ronde 7A). */
export function RailNav({ counts }: { counts: NavCounts }) {
  return (
    <nav className="flex flex-col gap-1.5">
      <RailNavItem to="album" label="Galerij" icon={<GridIcon className="size-4" />} />
      <RailNavItem
        to="challenges"
        label="Challenges"
        icon={<TargetIcon className="size-4" />}
        badge={counts.challenges}
      />
    </nav>
  )
}

function RailNavItem({
  to,
  label,
  icon,
  badge,
}: {
  to: string
  label: string
  icon: ReactNode
  badge?: number
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        'rounded-row flex h-11.5 items-center gap-3 px-3.5 text-[15px] ' +
        (isActive ? 'bg-tint text-accent font-semibold' : 'text-ink-muted hover:bg-tint/40')
      }
    >
      {icon}
      {label}

      {badge !== undefined && badge > 0 && (
        <span className="bg-accent text-accent-ink ml-auto rounded-full px-1.75 py-0.5 text-[11px] font-bold">
          {badge}
        </span>
      )}
    </NavLink>
  )
}
